'use client'

/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useState } from 'react'
import { Box } from '@acko-tech/building-blocks.ui.common'
import { Animate } from 'react-simple-animate'
import StyledComponentsRegistry from '@configs/registry'
import { ThemeProvider, AckoMainTheme } from '@acko-tech/building-blocks.ui.theme'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import GenericTab from '@components/MainWidget/CarBikeInTab/genericTab'
import { segmentEventCommonPropFunc } from '@utils/segmentProperties'
import {
  CAR,
  BIKE,
  getDynamicComponents,
  TAP_BTN_CAR_ICON,
  TAP_BTN_BIKE_ICON,
  MOTOR_FLOATING_WIDGET_TYPE,
  MOTOR_PURPLE_WIDGET_TYPE,
} from './constant'
import { getPurpleVariantProps } from './helper'
import { MultiWidgetProps } from './interfaces'
import { RootContainer, TabBarContainer, ProductContentContainer, StyleBox } from './styles'

const MultiProductWidget = ({
  widgetHeading,
  disclaimer,
  inputPlaceholder,
  widgetCtaText,
  segmentEventCommonProperties,
  newFlowBanner,
  reqCookie,
  isSemPage,
  isOldLayout,
  isBottomFloater,
  widgetVariant,
  regexPatternsData,
  widgetOptions,
}: MultiWidgetProps) => {
  const [selectedProduct, setSelectedProduct] = useState('car')
  // Flag to check if the user has switched tabs at least once
  const [hasInteracted, setHasInteracted] = useState(false)
  // Flag to trigger the animation on tab change
  const [animate, setAnimate] = useState(false)

  const DynamicComponent = getDynamicComponents[selectedProduct]

  const defaultValuesProps = {
    widgetHeading,
    disclaimer,
    inputPlaceholder,
    newFlowBanner,
    isSemPage,
    isOldLayout,
    segmentEventCommonProperties,
    widgetCtaText: widgetCtaText || 'Insure now',
    widgetVariant,
    regexPatternsData,
    isBottomFloater,
  }

  const purpleVariantAdditionalProps = getPurpleVariantProps(selectedProduct, defaultValuesProps, widgetOptions)

  const onTabChange = async tabVal => {
    if (!hasInteracted) {
      setHasInteracted(true)
    }
    setSelectedProduct(tabVal)
    // Trigger the animation by toggling the animate flag
    setAnimate(false)
    setTimeout(() => {
      setAnimate(true)
    }, 5)

    const eventName = tabVal === 'car' ? TAP_BTN_CAR_ICON : TAP_BTN_BIKE_ICON
    const properties = await segmentEventCommonPropFunc(segmentEventCommonProperties)
    trackEvent(eventName, {
      ...properties,
      widget_type: isBottomFloater ? MOTOR_FLOATING_WIDGET_TYPE : MOTOR_PURPLE_WIDGET_TYPE,
    })
  }

  const getContent = () => {
    switch (selectedProduct) {
      case CAR:
        return (
          <StyleBox isWidget={selectedProduct} maxWidthPurple="520px">
            <DynamicComponent
              key={selectedProduct}
              reqCookie={reqCookie}
              okind="tracker"
              {...purpleVariantAdditionalProps}
            />
          </StyleBox>
        )
      case BIKE:
        return (
          <StyleBox isWidget={selectedProduct} maxWidthPurple="520px">
            <DynamicComponent
              key={selectedProduct}
              reqCookie={reqCookie}
              okind="tracker"
              {...purpleVariantAdditionalProps}
            />
          </StyleBox>
        )
      default:
        return null
    }
  }

  return (
    <StyledComponentsRegistry>
      <ThemeProvider theme={AckoMainTheme}>
        <RootContainer>
          <TabBarContainer isFloater={isBottomFloater} justifyContent={isBottomFloater || 'flex-start'}>
            <GenericTab
              isActive={selectedProduct === CAR}
              handleClick={onTabChange}
              tabType={CAR}
              isBlog={false}
              isFloater={isBottomFloater}
            />
            <GenericTab
              isActive={selectedProduct === BIKE}
              handleClick={onTabChange}
              tabType={BIKE}
              isBlog={false}
              isFloater={isBottomFloater}
            />
          </TabBarContainer>
          <Box>
            {hasInteracted ? (
              <Animate
                key={selectedProduct} // Forces remount on each tab change
                play={animate}
                start={{ transform: 'translateY(30%)' }}
                end={{ transform: 'translateY(0)' }}
                duration={0.5} // 0.5 seconds equals 500ms
              >
                <ProductContentContainer>{getContent()}</ProductContentContainer>
              </Animate>
            ) : (
              <ProductContentContainer>{getContent()}</ProductContentContainer>
            )}
          </Box>
        </RootContainer>
      </ThemeProvider>
    </StyledComponentsRegistry>
  )
}

export default MultiProductWidget
